import React from 'react';
import {
	mergeStyleSets,
	getTheme,
	FontWeights,
	Modal,
	IconButton,
	IButtonStyles,
	FontSizes,
	IButtonProps,
	DefaultButton,
	PrimaryButton,
} from '@fluentui/react';
import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';

export interface IReusableDialogFooterProps {
	leftButtons?: IButtonProps[];
	rightButtons: IButtonProps[];
}

export interface IReusableDialogProps {
	state: ReusableDialogState;
	footerButtons?: IReusableDialogFooterProps;
}

class ReusableDialog extends React.Component<IReusableDialogProps> {
	private theme = getTheme();
	private contentStyles = mergeStyleSets({
		container: {
			display: 'flex',
			flexFlow: 'column nowrap',
			alignItems: 'stretch',
			width: this.props.state.dialogWidth,
			height: this.props.state.dialogHeight,
		},
		header: [
			{
				position: 'relative',
				flexDirection: 'row',
				borderTop: `4px solid ${this.theme.palette.themePrimary}`,
				color: this.theme.palette.neutralPrimary,
				display: 'flex',
				justifyContent: 'space-between',
				fontWeight: FontWeights.semibold,
				padding: '12px 12px 14px 24px',
			},
		],
		heading: {
			color: this.theme.palette.neutralPrimary,
			fontWeight: FontWeights.semibold,
			fontSize: FontSizes.large,
			margin: '0',
		},
		body: {
			flex: '4 4 auto',
			padding: '0 24px 24px 24px',
			overflowY: 'hidden',
			selectors: {
				p: { margin: '14px 0' },
				'p:first-child': { marginTop: 0 },
				'p:last-child': { marginBottom: 0 },
			},
		},
		footer: {
			position: 'absolute',
			bottom: 0,
			left: 0,
			width: '100%',
		},
		footerButtons: {
			display: 'flex',
			marginBottom: '20px',
			marginLeft: '20px',
			marginRight: '20px',
			justifyContent: 'space-between',
		},
		footerLeftButtons: {
			display: 'flex',
			flexDirection: 'row',
			gap: '10px',
		},
		footerRightButtons: {
			marginLeft: 'auto',
			display: 'flex',
			flexDirection: 'row',
			gap: '10px',
		},
	});

	iconButtonStyles: Partial<IButtonStyles> = {
		root: {
			color: this.theme.palette.neutralPrimary,
			marginLeft: 'auto',
			marginTop: '4px',
			marginRight: '2px',
		},
		rootHovered: {
			color: this.theme.palette.neutralDark,
		},
	};

	render() {
		return (
			<>
				<Modal
					onDismiss={() => {
						this.props.state.onDismiss();
					}}
					isOpen={this.props.state.visible}
					containerClassName={this.contentStyles.container}
				>
					<div className={this.contentStyles.header}>
						<div>
							{this.props.state.miniTitle && <div style={{fontSize:12, color:this.theme.palette.neutralSecondary}}>{this.props.state.miniTitle}</div>}
							<h2 className={this.contentStyles.heading}>{this.props.state.title}</h2>
						</div>
						<IconButton
							id="closeSettingsDialog"
							styles={this.iconButtonStyles}
							iconProps={{ iconName: 'Cancel' }}
							ariaLabel="Close dialog"
							onClick={() => {
								this.props.state.onDismiss();
							}}
						/>
					</div>
					<div className={this.contentStyles.body}>{this.props.children}</div>
					{this.props.footerButtons && (
						<div className={this.contentStyles.footer}>
							<div className={this.contentStyles.footerButtons}>
								{this.props.footerButtons.leftButtons &&
									this.props.footerButtons.leftButtons.length > 0 && (
										<div className={this.contentStyles.footerLeftButtons}>
											{this.props.footerButtons.leftButtons.map((props) => {
												return <DefaultButton {...props} />;
											})}
										</div>
									)}
								{this.props.footerButtons.rightButtons.length > 0 && (
									<div className={this.contentStyles.footerRightButtons}>
										{this.props.footerButtons.rightButtons.slice(0, -1).map((props) => {
											return <DefaultButton {...props} />;
										})}
										<PrimaryButton {...this.props.footerButtons.rightButtons.at(-1)} />
									</div>
								)}
							</div>
						</div>
					)}
				</Modal>
			</>
		);
	}
}

export default observer(ReusableDialog);

export class ReusableDialogState {
	public dialogWidth: number|string = 800;
	public dialogHeight?: number;
	public dialogPadding: number = 0;

	public title: string;
	public setTitle(title: string) {
		this.title = title;
	}

	public miniTitle:string = '';
	public setMiniTitle(miniTitle: string) {
		this.miniTitle = miniTitle;
	}

	public visible: boolean = false;
	public setVisible(visible: boolean) {
		this.visible = visible;
	}

	private _onDismiss: () => void = () => {};

	public setOnDismiss(onDismiss: () => void) {
		this._onDismiss = onDismiss;
	}

	constructor(title?: string, dialogHeight?: number) {
		this.title = title || '';
		this.dialogHeight = dialogHeight;

		makeAutoObservable(this);
	}

	public setWidth(width: number|string) {
		this.dialogWidth = width;
	}

	public onDismiss() {
		this.setVisible(false);
		this._onDismiss();
	}
}
